import React from 'react';
import Svg, { Circle, G, Path, Rect } from 'react-native-svg';
import { D } from './theme';

/**
 * 브랜드 마크와 앱에서 쓰는 그림들. 전부 SVG 라 어느 크기에서도 선이 깨지지 않는다.
 *
 * 마크의 뜻: 가격표(딜) 안에 조준점(찾아 준다). 이 앱이 하는 일 그대로다.
 * 아이콘 폰트로는 이걸 표현할 수 없어서 마크만 직접 그린다.
 *
 * 배색은 피드와 같다 — 검은 판, 형광 노랑은 '이 앱이 해 주는 일' 에만. 모서리는 각지게.
 */
const TAG = 'M14 6H42V42H6V14L14 6Z';

export function Logo({ size = 48, tone = D.ink, mark = D.volt }: { size?: number; tone?: string; mark?: string }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      {/* 가격표: 왼쪽 위 모서리를 잘라 낸 사각형 */}
      <Path d={TAG} fill={tone} />
      {/* 끈 구멍 */}
      <Circle cx="14.5" cy="14.5" r="3.2" fill={mark} />
      {/* 조준점: 찾아 준다는 뜻 */}
      <Circle cx="26" cy="28" r="7" stroke={mark} strokeWidth="2.8" fill="none" />
      <Path d="M31 33l6 6" stroke={mark} strokeWidth="2.8" strokeLinecap="round" />
    </Svg>
  );
}

/** 이름까지 있는 가로형. 첫 화면과 빈 화면 머리에 쓴다. */
export function Wordmark({ size = 34 }: { size?: number }) {
  return (
    <Svg width={size * 5.6} height={size} viewBox="0 0 190 34" fill="none">
      <G>
        <Path d="M10 4H30V30H4V10L10 4Z" fill={D.ink} />
        <Circle cx="10.3" cy="10.3" r="2.3" fill={D.volt} />
        <Circle cx="18.4" cy="19.8" r="5" stroke={D.volt} strokeWidth="2" fill="none" />
        <Path d="M21.9 23.3l4.3 4.3" stroke={D.volt} strokeWidth="2" strokeLinecap="round" />
      </G>
      {/* 딜헌터 — 글자는 폰트에 기대지 않고 도형으로 고정한다 */}
      <Path d="M42 8h38v3.4H42zM42 15.6h38V19H42zM42 23.2h26v3.4H42z" fill={D.ink} />
      <Rect x="86" y="8" width="3.4" height="18.6" fill={D.ink} />
    </Svg>
  );
}

/** 튜토리얼 1 — 흩어진 특가를 한곳에 모은다 */
export function ArtCollect({ size = 88 }: { size?: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 88 88" fill="none">
      <Rect x="7" y="11" width="26" height="17" fill={D.panel} stroke={D.ink} strokeWidth="2" />
      <Rect x="39" y="5" width="22" height="15" fill={D.panel} stroke={D.ink} strokeWidth="2" />
      <Rect x="65" y="17" width="17" height="13" fill={D.panel} stroke={D.ink} strokeWidth="2" />
      <Path d="M20 30v11h34a8 8 0 0 1 8 8v4" stroke={D.ink3} strokeWidth="2.2" strokeLinecap="round" fill="none" />
      <Rect x="20" y="54" width="48" height="26" fill={D.ink} />
      <Path d="M29 65h14M29 72h22" stroke={D.volt} strokeWidth="3" strokeLinecap="round" />
    </Svg>
  );
}

/** 튜토리얼 2 — 말로 적으면 AI 가 되물어 좁혀 준다 */
export function ArtAsk({ size = 88 }: { size?: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 88 88" fill="none">
      <Path d="M6 16h58v30H30l-13 10V46H6z" fill={D.panel} stroke={D.ink} strokeWidth="2" />
      <Path d="M17 28h32M17 36h20" stroke={D.ink} strokeWidth="3" strokeLinecap="round" />
      <Rect x="50" y="46" width="32" height="32" fill={D.volt} stroke={D.ink} strokeWidth="2" />
      <Path d="M58 62l5 5 11-13" stroke={D.ink} strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </Svg>
  );
}

/** 튜토리얼 3 — 싸게 뜨면 알려 준다 */
export function ArtAlert({ size = 88 }: { size?: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 88 88" fill="none">
      <Path d="M44 13a17 17 0 0 1 17 17v13l6 10H21l6-10V30a17 17 0 0 1 17-17z" fill={D.panel} stroke={D.ink} strokeWidth="2" />
      <Path d="M36 59a8 8 0 0 0 16 0" stroke={D.ink} strokeWidth="3" strokeLinecap="round" fill="none" />
      <Rect x="55" y="8" width="26" height="20" fill={D.volt} stroke={D.ink} strokeWidth="2" />
      <Path d="M61 18l4 4 10-9" stroke={D.ink} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <Path d="M18 72h52" stroke={D.ink3} strokeWidth="3" strokeLinecap="round" />
    </Svg>
  );
}

/** 카카오 로그인 안내 그림 — 가입하면 뭐가 달라지는지. 카카오 노랑은 브랜드라 그대로 둔다. */
export function ArtSignup({ size = 96 }: { size?: number }) {
  return (
    <Svg width={size} height={size * 0.62} viewBox="0 0 96 60" fill="none">
      <Rect x="3" y="9" width="38" height="42" fill={D.panel} stroke={D.ink} strokeWidth="2" />
      <Path d="M12 22h20M12 30h14M12 38h8" stroke={D.ink3} strokeWidth="3" strokeLinecap="round" />
      <Path d="M47 30h12M55 25l5 5-5 5" stroke={D.ink} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <Rect x="55" y="5" width="38" height="50" fill="#FEE500" stroke={D.ink} strokeWidth="2" />
      <Path d="M64 20h20M64 29h20M64 38h12" stroke="#191600" strokeWidth="3" strokeLinecap="round" />
    </Svg>
  );
}
